import { useEffect, useRef, useState } from "react";
import Plot from "react-plotly.js";
import useProjectStore from "../../store/projectStore";
import { geometryApi } from "../../api/blades";
import { bladeMesh, revolutionMesh } from "../../utils/bladeMesh";
import { useChartColors } from "../../chartTheme";

export default function View3DPage() {
  const { COLOR_DARK, COLOR_LIGHT } = useChartColors();
  const { activeDesign } = useProjectStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cameraRef = useRef({ eye: { x: 1.5, y: 1.5, z: 0.8 } });

  useEffect(() => {
    if (!activeDesign?.id) return;
    setLoading(true);
    setError(null);
    geometryApi.bladeViews(activeDesign.id)
      .then(({ data }) => setData(data))
      .catch(e => setError(e?.response?.data?.error || "Computation failed"))
      .finally(() => setLoading(false));
  }, [activeDesign?.id, activeDesign?.updated_at]);

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;
  if (loading)        return <div className="p-6 text-sm text-gray-400">Computing 3D view…</div>;
  if (error)          return <div className="p-6 text-sm text-red-500">{error}</div>;
  if (!data?.surface_3d) return <div className="p-6 text-sm text-gray-400">No geometry data yet. Complete the MECO and Beta tabs first.</div>;

  const { blade, hub, shroud } = data.surface_3d;
  const nBlades = activeDesign.n_blades || 6;
  const traces = [];

  if (blade)
    traces.push(bladeMesh(blade, nBlades, COLOR_DARK, "Blade", 0.90));

  if (hub)
    traces.push(revolutionMesh(hub.x, hub.z, COLOR_DARK, "Hub", 0.35));

  if (shroud)
    traces.push(revolutionMesh(shroud.x, shroud.z, COLOR_LIGHT, "Shroud", 0.18));

  if (data.cutoff_line)
    traces.push(revolutionMesh(data.cutoff_line.r, data.cutoff_line.z, "#374151", "Cut-off", 0.15));

  return (
    <div className="flex-1 p-4" style={{ height: "calc(100vh - 160px)" }}>
      <Plot
        data={traces}
        layout={{
          margin: { l: 0, r: 0, t: 0, b: 0 },
          scene: {
            xaxis: { title: "x [mm]", gridcolor: "#e5e7eb" },
            yaxis: { title: "y [mm]", gridcolor: "#e5e7eb" },
            zaxis: { title: "z [mm]", gridcolor: "#e5e7eb" },
            aspectmode: "data",
            camera: cameraRef.current,
          },
          paper_bgcolor: "transparent",
          showlegend: true,
          legend: { font: { size: 11 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
        }}
        onRelayout={(e) => { if (e["scene.camera"]) cameraRef.current = e["scene.camera"]; }}
        config={{ displayModeBar: "hover", modeBarButtons: [["zoom3d", "pan3d", "orbitRotation", "resetCameraDefault3d", "toImage"]], scrollZoom: true, responsive: true, displaylogo: false }}
        style={{ width: "100%", height: "100%" }}
        useResizeHandler
      />
    </div>
  );
}
